import { getNodeMovesMap } from 'diagram/nodeMoves';
import { getSankeyTitle } from 'diagram/title';
import { Sankey } from 'generated-api';

import { isNumeric } from './number';
import { sankeyFormFlowsMapToArray, sankeyFormNodesMapToArray } from './sankey';

/*
 * Populates the chart field of the Sankey model with the values from the form.
 */
export const getChartFromSankeyFormValues = ({
  company_name,
  comparison_line_prefix,
  comparison_line_suffix,
  comparison_line_value_type,
  custom_title,
  fiscal_year,
  flows,
  font_family,
  font_size,
  label_pos,
  node_width,
  node_height,
  node_spacing,
  nodes,
  nr_of_decimals,
  remembered_node_moves,
  reporting_period,
  second_label_line_value_type,
  show_comparison_line,
  show_imbalances,
  show_labels,
  show_title,
  statement_type,
  title_font_color,
  title_font_size,
  unit_prefix,
  unit_suffix,
  use_custom_title,
}: SankeyFormValues): Sankey['chart'] => {
  // Easier nodes and flows access
  const nodesArray = sankeyFormNodesMapToArray(nodes);
  const flowsArray = sankeyFormFlowsMapToArray(flows);
  const getNodeByName = (nodeName: string) =>
    nodesArray.find((node) => node.name === nodeName);
  // Helpers for node moves
  const movesMap = getNodeMovesMap(remembered_node_moves || '[]');
  const getNodeOffset = (nodeName: string) => ({
    x: movesMap.get(nodeName)?.[0] ?? 0,
    y: movesMap.get(nodeName)?.[1] ?? 0,
  });
  // Title value generation (ignoring custom title override)
  const titleValue = getSankeyTitle({
    company_name,
    fiscal_year,
    reporting_period,
    statement_type,
    use_custom_title: false,
    custom_title: '',
  });

  // Build chart JSON
  return {
    type: 'sankey',
    version: 1,
    settings: {
      font_family: font_family ?? 'Inter',
      label_comparison_value_prefix: comparison_line_prefix ?? '',
      label_comparison_value_suffix: comparison_line_suffix ?? ' Y/Y',
      label_comparison_value_type: comparison_line_value_type ?? 'percent',
      label_font_size: font_size ?? 10,
      label_placement: label_pos ?? 'outside',
      label_value_number_of_decimals:
        nr_of_decimals === 'null' ? 'all' : (nr_of_decimals ?? 'all'),
      label_value_prefix: unit_prefix ?? '$',
      label_value_suffix: unit_suffix ?? 'M',
      label_value_type: second_label_line_value_type ?? 'amount',
      node_width: node_width ?? 30,
      node_height: node_height ?? 20,
      node_spacing: node_spacing ?? 40,
      show_comparison_value: show_comparison_line ?? true,
      show_imbalances: show_imbalances ?? true,
      show_labels: show_labels ?? true,
      statement_type:
        statement_type === 'null' ? 'income' : (statement_type ?? 'income'),
    },
    title: {
      color: title_font_color ?? '#366b87',
      font_size: title_font_size ?? 32,
      value: titleValue,
      value_override: use_custom_title ? (custom_title ?? '') : '',
      visible: show_title ?? true,
    },
    data: {
      nodes: nodesArray.map((node) => ({
        id: node.id,
        name: node.name,
        color: node.color ?? '#696969',
        offset: getNodeOffset(node.name),
        label_value_override: node.use_custom_amount_label
          ? node.custom_amount_label || ' '
          : '',
        label_comparison_value_override: node.use_custom_comparison_label
          ? node.custom_comparison_label || ' '
          : '',
      })),
      flows: flowsArray.map((flow) => ({
        id: flow.id,
        color: getNodeByName(flow.target)?.color ?? '#696969',
        source_node_id: getNodeByName(flow.source)?.id,
        target_node_id: getNodeByName(flow.target)?.id,
        value: isNumeric(flow.amount) ? flow.amount : undefined,
        value_comparison: isNumeric(flow.amountComparison)
          ? flow.amountComparison
          : undefined,
        data_grid_row_index: flow.dataGridRowIndex,
      })),
    },
  };
};
